import { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router';
import { Banner, Button, EmptyState, Segmented, Skeleton, useToast } from '../../../ds/components';
import { ApiError, errorMessage } from '../../../lib/api';
import { usePermissions } from '../../../lib/session';
import { ControllingTabs } from '../ControllingTabs';
import { ScopeMenu } from '../ScopeMenu';
import figures from '../figures/figures.module.css';
import {
  downloadSales,
  isSalesPeriod,
  isSalesScope,
  useSales,
  type SalesPeriod,
  type SalesScope,
} from './api';
import { SalesView } from './SalesView';
import styles from './sales.module.css';

/**
 * Controlling „Vertrieb" (`/controlling/sales?period=&scope=&userId=`, Nico feedback FB-002, D-323…D-326): per sales
 * partner the appointments, outcomes and the deals that followed. „Alle Partner" / „Nur ich" switches between the
 * widest scope of the viewer (team or organization, refined by the scope menu) and `me`; „Alle Partner" needs
 * `controlling.figures` beyond `own`. A click on a partner row lists that partner's appointments below the table.
 */

export const SALES_PERIOD_ITEMS: { value: SalesPeriod; label: string }[] = [
  { value: 'week', label: 'Woche' },
  { value: 'month', label: 'Monat' },
  { value: 'quarter', label: 'Quartal' },
  { value: 'year', label: 'Jahr' },
];

export type SalesViewMode = 'all' | 'me';

export const SALES_VIEW_ITEMS: { value: SalesViewMode; label: string }[] = [
  { value: 'all', label: 'Alle Partner' },
  { value: 'me', label: 'Nur ich' },
];

/** „Alle Partner" exists for viewers whose controlling scope reaches beyond themselves. */
export function canSeeAllPartners(permission: string | undefined): boolean {
  return permission === 'span' || permission === 'organization';
}

/** „Alle Partner" / „Nur ich"; nothing for viewers who see only themselves. */
export function SalesViewToggle({
  allowed,
  value,
  onChange,
}: {
  allowed: boolean;
  value: SalesViewMode;
  onChange: (value: SalesViewMode) => void;
}) {
  if (!allowed) return null;
  return (
    <Segmented
      label="Ansicht"
      className={styles.toggle}
      items={SALES_VIEW_ITEMS}
      value={value}
      onChange={onChange}
    />
  );
}

export function SalesPage() {
  const navigate = useNavigate();
  const { toast } = useToast();
  const permissions = usePermissions();
  const permitted = canSeeAllPartners(permissions.set['controlling.figures']);
  const [search, setSearch] = useSearchParams();
  const periodParam = search.get('period');
  const scopeParam = search.get('scope');
  const period: SalesPeriod = isSalesPeriod(periodParam) ? periodParam : 'month';
  const requestedScope: SalesScope | null = isSalesScope(scopeParam) ? scopeParam : null;
  const scope: SalesScope | null = permitted ? requestedScope : 'me';
  const userId = scope === 'me' ? null : search.get('userId');
  const filter = { period, scope, userId };
  const sales = useSales(filter);
  const [exporting, setExporting] = useState(false);
  const data = sales.data;
  const allScopes = (data?.scopes ?? []).filter((item) => item.key !== 'me');
  // A lead without subordinates is offered `me` only — no „Alle Partner" then.
  const allowed = permitted && (data === undefined || allScopes.length > 0);
  const view: SalesViewMode = (data?.scope ?? scope) === 'me' ? 'me' : 'all';

  function update(changes: Partial<Record<'period' | 'scope' | 'userId', string | null>>) {
    setSearch(
      (current) => {
        const params = new URLSearchParams(current);
        for (const [key, value] of Object.entries(changes)) {
          if (value === null || value === undefined) params.delete(key);
          else params.set(key, value);
        }
        return params;
      },
      { replace: true },
    );
  }

  async function exportCsv() {
    if (exporting) return;
    setExporting(true);
    try {
      await downloadSales({ ...filter, scope: scope ?? data?.scope ?? null });
    } catch (error) {
      toast({ message: errorMessage(error) });
    } finally {
      setExporting(false);
    }
  }

  const forbidden = sales.error instanceof ApiError && sales.error.status === 403;

  const head = (
    <div className={figures.head}>
      <div className={figures.headText}>
        <div className={figures.eyebrow}>Controlling</div>
        <h1 className={figures.title}>{data?.header.title ?? 'Vertrieb'}</h1>
        <div className={figures.line}>{data ? data.header.line : <Skeleton width={260} height={12} />}</div>
      </div>
      <div className={figures.headActions}>
        <SalesViewToggle
          allowed={allowed}
          value={view}
          onChange={(value) =>
            update({ scope: value === 'me' ? 'me' : (allScopes[0]?.key ?? null), userId: null })
          }
        />
        <Segmented
          label="Zeitraum"
          items={SALES_PERIOD_ITEMS}
          value={period}
          onChange={(value) => update({ period: value })}
        />
        {data && view === 'all' ? (
          <ScopeMenu
            scopes={allScopes}
            scope={data.scope}
            onChange={(value) => update({ scope: value, userId: null })}
          />
        ) : null}
        <Button variant="secondary" loading={exporting} disabled={!data} onClick={() => void exportCsv()}>
          Export
        </Button>
      </div>
    </div>
  );

  if (forbidden) {
    return (
      <div className={figures.page}>
        <ControllingTabs tab="sales" />
        <EmptyState
          eyebrow="Controlling"
          title="Vertrieb"
          description={errorMessage(sales.error)}
          actions={
            <Button variant="secondary" onClick={() => void navigate('/controlling/sales?scope=me')}>
              Nur ich
            </Button>
          }
        />
      </div>
    );
  }

  return (
    <div className={figures.page} aria-busy={sales.isFetching ? true : undefined}>
      {head}
      <ControllingTabs tab="sales" />
      {sales.isError && !data ? (
        <Banner
          tone="danger"
          actions={
            <Button variant="secondary" size="small" onClick={() => void sales.refetch()}>
              Erneut laden
            </Button>
          }
        >
          {errorMessage(sales.error)}
        </Banner>
      ) : null}
      {sales.isPending ? (
        <div className={figures.skeletonStack} aria-busy="true" aria-label="Wird geladen">
          <Skeleton variant="block" height={96} />
          <Skeleton variant="block" height={240} />
        </div>
      ) : null}
      {data ? (
        <SalesView data={data} selectedUserId={userId} onSelect={(next) => update({ userId: next })} />
      ) : null}
    </div>
  );
}
